import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getImpersonationMetadata,
  isImpersonating,
  resolveImpersonationMutationConfirm,
  stopImpersonationSession
} from '@/shared/services/helpers/impersonation/impersonation-helper.service';

export function ImpersonationBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const [metadata, setMetadata] = useState(getImpersonationMetadata());
  const [stopping, setStopping] = useState(false);

  useEffect(() => {
    const sync = () => setMetadata(getImpersonationMetadata());
    window.addEventListener('impersonation-session-changed', sync);
    return () => window.removeEventListener('impersonation-session-changed', sync);
  }, []);

  if (!isImpersonating() || !metadata) return null;

  const handleStop = async () => {
    setStopping(true);
    try {
      await stopImpersonationSession();
      window.dispatchEvent(new Event('impersonation-session-changed'));
      window.location.href = '/dashboard';
    } finally {
      setStopping(false);
    }
  };

  return (
    <Box sx={{ px: 3, pt: 2 }}>
      <Alert
        severity="warning"
        action={
          <Button color="inherit" size="small" disabled={stopping} onClick={handleStop}>
            {t('USER_MANAGEMENT_MODULE.IMPERSONATION.STOP')}
          </Button>
        }
      >
        <Typography variant="body2">
          {t('USER_MANAGEMENT_MODULE.IMPERSONATION.BANNER', {
            targetName: metadata.target.name,
            targetEmail: metadata.target.email,
            actorName: metadata.actor.name,
            reason: metadata.reason
          })}
        </Typography>
      </Alert>
    </Box>
  );
}

export function ImpersonationMutationConfirmDialog(): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<{ method: string; url: string } | null>(null);

  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{ method: string; url: string }>;
      setDetail(customEvent.detail);
      setOpen(true);
    };
    window.addEventListener('impersonation-mutation-confirm', handler);
    return () => window.removeEventListener('impersonation-mutation-confirm', handler);
  }, []);

  const close = (allowed: boolean) => {
    setOpen(false);
    setDetail(null);
    resolveImpersonationMutationConfirm(allowed);
  };

  return (
    <Dialog open={open} onClose={() => close(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{t('USER_MANAGEMENT_MODULE.IMPERSONATION.MUTATION_TITLE')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t('USER_MANAGEMENT_MODULE.IMPERSONATION.MUTATION_BODY')}
        </Typography>
        {detail && (
          <Typography variant="caption" color="text.secondary">
            {detail.method.toUpperCase()} {detail.url}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => close(false)}>{t('USER_MANAGEMENT_MODULE.ACTIONS.CANCEL')}</Button>
        <Button variant="contained" color="warning" onClick={() => close(true)}>
          {t('USER_MANAGEMENT_MODULE.IMPERSONATION.MUTATION_CONFIRM')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
